import { useMemo } from 'react';
import {
  Dropdown,
  Field,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { FIELD_LABEL_LAYOUT, useFetch } from '@moko/ui';
import { useTranslation } from 'react-i18next';
import {
  getCurrencies,
  toTenantCurrencyPayload,
  type ICatalogCurrency,
  type ITenantCurrency,
} from '../../services/tenantService';

export interface TenantCurrencyFieldsProps {
  value: ITenantCurrency[];
  onChange: (currencies: ITenantCurrency[]) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    width: '100%',
    maxWidth: '520px',
    boxSizing: 'border-box',
  },
  field: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
});

const currencyLabel = (currency: ICatalogCurrency): string =>
  currency.symbol ? `${currency.name} (${currency.symbol})` : currency.name;

const TenantCurrencyFields = ({
  value,
  onChange,
  disabled = false,
}: TenantCurrencyFieldsProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const catalogFetch = useFetch<ICatalogCurrency[]>({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  });

  const catalog = catalogFetch.data ?? [];
  const catalogError = catalogFetch.error
    ? t('currenciesLoadFailed', 'Could not load currencies.')
    : undefined;
  const selectedCodes = value.map((item) => item.currencyCode);
  const defaultCode = value.find((item) => item.isDefault)?.currencyCode ?? '';

  const selectedCatalog = useMemo(() => {
    const byCode = new Map(catalog.map((item) => [item.code, item]));
    return selectedCodes.map(
      (code) => byCode.get(code) ?? { code, name: code, symbol: '' }
    );
  }, [catalog, selectedCodes]);

  const currenciesLabel = selectedCatalog.map(currencyLabel).join(', ');
  const defaultItem = selectedCatalog.find((item) => item.code === defaultCode);
  const defaultLabel = defaultItem ? currencyLabel(defaultItem) : defaultCode;

  const update = (nextCodes: string[], nextDefault: string) => {
    const nextDefaultCode = nextCodes.includes(nextDefault) ? nextDefault : '';
    onChange(
      toTenantCurrencyPayload(nextCodes, nextDefaultCode, value, catalog)
    );
  };

  return (
    <div className={styles.root}>
      <Field
        className={styles.field}
        label={t('currencies', 'Currencies')}
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
        validationState={catalogError ? 'error' : undefined}
        validationMessage={catalogError}
      >
        <Dropdown
          style={{ minWidth: 'auto', width: '100%' }}
          multiselect
          disabled={disabled || catalogFetch.loading || Boolean(catalogError)}
          selectedOptions={selectedCodes}
          value={currenciesLabel}
          placeholder={t('selectCurrencies', 'Select currencies')}
          onOptionSelect={(_, data) =>
            update(data.selectedOptions, defaultCode)
          }
        >
          {[
            ...catalog,
            ...selectedCatalog.filter(
              (item) => !catalog.some((currency) => currency.code === item.code)
            ),
          ].map((currency) => (
            <Option key={currency.code} value={currency.code}>
              {currencyLabel(currency)}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field
        className={styles.field}
        label={t('defaultCurrency', 'Default currency')}
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
      >
        <Dropdown
          style={{ minWidth: 'auto', width: '100%' }}
          disabled={disabled || selectedCodes.length === 0}
          selectedOptions={defaultCode ? [defaultCode] : []}
          value={defaultLabel}
          placeholder={t('selectDefaultCurrency', 'Select default currency')}
          onOptionSelect={(_, data) =>
            update(selectedCodes, data.optionValue ?? '')
          }
        >
          {selectedCatalog.map((currency) => (
            <Option key={currency.code} value={currency.code}>
              {currencyLabel(currency)}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </div>
  );
};

export default TenantCurrencyFields;
